import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, message, Tag, Tooltip, Form, Col, Select, Input, Space, DatePicker, Modal, Image } from 'antd';
import { EyeOutlined, PlusOutlined, EditOutlined, DeleteOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { 
  StoreDisplayGroupPlanogramApply, 
  StoreDisplayGroupPlanogramApplyFilters, 
  PaginationParams,
  ApplyStatus,
  APPLY_STATUS_COLORS,
  AuditStoreDisplayGroupPlanogramApplyRequest,
  TeamType
} from '../../../types/storeDisplayGroupPlanogramApply';
import StoreDisplayGroupPlanogramApplyService from '../../../services/storeDisplayGroupPlanogramApplyService';
import { StoreGroupService } from '../../../services/storeGroupService';
import categoryService from '../../../services/categoryService';
import FilterPanel from '../../../components/common/FilterPanel';
import EmptyState from '../../../components/common/EmptyState';
import NetworkError from '../../../components/common/NetworkError';
import { isRetryableError, sanitizeErrorForDisplay } from '../../../utils/errorHandler';

export const PAGE_SIZES = [10, 20, 50];
export const DEFAULT_PAGE_SIZE = 20;

const { RangePicker } = DatePicker;

const StoreDisplayGroupPlanogramApplyList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  
  // State management
  const [data, setData] = useState<StoreDisplayGroupPlanogramApply[]>([]);
  const [loading, setLoading] = useState(false);
  const [storeGroupOptionsLoading, setStoreGroupOptionsLoading] = useState(false);
  const [displayGroupOptionsLoading, setDisplayGroupOptionsLoading] = useState(false);
  const [divisionOptionsLoading, setDivisionOptionsLoading] = useState(false);
  const [auditLoading, setAuditLoading] = useState<number | null>(null);
  const [storeGroupOptions, setStoreGroupOptions] = useState<Array<{ code: string; name: string }>>([]);
  const [displayGroupOptions, setDisplayGroupOptions] = useState<Array<{ code: string; name: string }>>([]);
  const [divisionOptions, setDivisionOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [auditModalVisible, setAuditModalVisible] = useState(false);
  const [auditingApply, setAuditingApply] = useState<StoreDisplayGroupPlanogramApply | null>(null);
  
  // Form for audit modal
  const [auditForm] = Form.useForm();
  
  // Filter and pagination state
  const [filters, setFilters] = useState<StoreDisplayGroupPlanogramApplyFilters>({teamType: TeamType.PLANOGRAM});
  const [pagination, setPagination] = useState<PaginationParams>({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    sortBy: 'gmtCreate',
    sortOrder: 'desc'
  });

  // Load dropdown options
  const loadOptions = useCallback(async () => {
    try {
      setStoreGroupOptionsLoading(true);
      setDisplayGroupOptionsLoading(true);
      setDivisionOptionsLoading(true);
      
      const storeGroupService = StoreGroupService.getInstance();
      
      const [storeGroupResponse, displayGroupResponse, divisionResponse] = await Promise.all([
        storeGroupService.listStoreGroup({}),
        StoreDisplayGroupPlanogramApplyService.getDisplayGroupCodeOptions(),
        categoryService.listDivision()
      ]);
      
      if (storeGroupResponse.success && storeGroupResponse.data) {
        setStoreGroupOptions(storeGroupResponse.data.map(group => ({
          code: group.code,
          name: group.name
        })));
      }
      
      if (displayGroupResponse.success && displayGroupResponse.data) {
        setDisplayGroupOptions(displayGroupResponse.data);
      }
      
      if (divisionResponse.success && divisionResponse.data) {
        setDivisionOptions(divisionResponse.data.map(division => ({
          value: division.value,
          label: division.label
        })));
      }
    } catch (error) {
      console.error('获取选项数据失败:', error);
    } finally {
      setStoreGroupOptionsLoading(false);
      setDisplayGroupOptionsLoading(false);
      setDivisionOptionsLoading(false);
    }
  }, []);

  // Load apply data
  const loadData = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);
    
    try {
      let req = filters;
      req.source = "benchmark";
      const response = await StoreDisplayGroupPlanogramApplyService.getApplies(filters, pagination);
      
      if (response.success && response.data) {
        setData(response.data.tableData);
        setTotal(response.data.paging.totalCount);
      } else {
        throw new Error(response.errorMessage || '获取数据失败');
      }
    } catch (error) {
      const errorMessage = sanitizeErrorForDisplay(error);
      setError(errorMessage);
      setData([]);
      setTotal(0);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [filters, pagination]);

  // Initial data load
  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle filter changes
  const handleFiltersChange = useCallback((values: any) => {
    const newFilters: StoreDisplayGroupPlanogramApplyFilters = {
      applyNo: values.applyNo,
      storeGroupCode: values.storeGroupCode,
      storeGroupName: values.storeGroupName,
      displayGroupCode: values.displayGroupCode,
      teamType: TeamType.PLANOGRAM,
      applyStatus: values.applyStatus,
      applyPerson: values.applyPerson,
      mainDivisionCode: values.mainDivisionCode,
      startDate: values.dateRange?.[0]?.format('YYYY-MM-DD HH:mm:ss'),
      endDate: values.dateRange?.[1]?.format('YYYY-MM-DD HH:mm:ss'),
      search: values.search
    };
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  }, []);

  // Handle reset filters
  const handleReset = useCallback(() => {
    form.resetFields();
    setFilters({ teamType: TeamType.PLANOGRAM});
    setPagination({
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      sortBy: 'gmtCreate',
      sortOrder: 'desc'
    });
    return {}; // 返回空对象作为重置后的表单值
  }, []);

  // Handle table pagination change
  const handleTableChange = useCallback((paginationConfig: any, _filters: any, sorter: any) => {
    const newPagination: PaginationParams = {
      page: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    };

    // Handle sorting
    if (sorter.field && sorter.order) {
      newPagination.sortBy = sorter.field;
      newPagination.sortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
    }

    setPagination(newPagination);
  }, []);

  // Handle view detail
  const handleViewDetail = useCallback((id: number) => {
    navigate(`/sdg-planogram-apply/${id}?returnTo=/sg-planogram-apply-4-planogram`);
  }, [navigate]);

  // Handle audit apply
  const handleAuditApply = useCallback((apply: StoreDisplayGroupPlanogramApply) => {
    setAuditingApply(apply);
    setAuditModalVisible(true);
    auditForm.resetFields();
  }, [auditForm]);

  // Handle audit submit
  const handleAuditSubmit = useCallback(async (values: any) => {
    if (!auditingApply) return;
    
    setAuditLoading(auditingApply.id);
    try {
      const request: AuditStoreDisplayGroupPlanogramApplyRequest = {
        id: auditingApply.id,
        applyStatus: values.applyStatus,
        auditRemark: values.auditRemark
      };
      
      const response = await StoreDisplayGroupPlanogramApplyService.auditApply(request);
      if (response.success) {
        message.success(t('storeDisplayGroupPlanogramApply.auditSuccess'));
        setAuditModalVisible(false);
        setAuditingApply(null);
        loadData(false);
      } else {
        message.error(response.errorMessage || t('storeDisplayGroupPlanogramApply.auditError'));
      }
    } catch (error) {
      message.error(t('storeDisplayGroupPlanogramApply.auditError'));
    } finally {
      setAuditLoading(null);
    }
  }, [auditingApply, t, loadData]);

  // Handle retry for network errors
  const handleRetry = useCallback(() => {
    loadData();
  }, [loadData]);

  // Format datetime display
  const formatDateTime = (dateTime: string): string => {
    return dayjs(dateTime).format('YYYY-MM-DD HH:mm:ss');
  };

  // Format date display
  const formatDate = (date?: string): string => {
    return date ? dayjs(date).format('YYYY-MM-DD') : '-';
  };

  // Render apply status tag
  const renderApplyStatus = (status: ApplyStatus) => {
    const color = APPLY_STATUS_COLORS[status];
    return (
      <Tag color={color}>
        {t(`storeDisplayGroupPlanogramApply.status.${status.toLowerCase()}`)}
      </Tag>
    );
  };

  // Table columns definition
  const columns: ColumnsType<StoreDisplayGroupPlanogramApply> = [
    {
      title: t('storeDisplayGroupPlanogramApply.applyNo'),
      dataIndex: 'applyNo',
      key: 'applyNo',
      // sorter: true,
      // sortOrder: pagination.sortBy === 'applyNo' ? (pagination.sortOrder === 'asc' ? 'ascend' : 'descend') : null,
      width: 180,
    },
    {
      title: t('storeDisplayGroupPlanogramApply.storeGroupInfo'),
      key: 'storeGroupInfo',
      render: (_, record: StoreDisplayGroupPlanogramApply) => (
        <div>
          <div>{record.storeGroupCode}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.storeGroupName}</div>
        </div>
      ),
      width: 150,
    },
   {
      title: t('common.division'),
      dataIndex: 'mainDivisionName',
      key: 'mainDivisionName',
      width: 180,
    },
    {
      title: t('storeDisplayGroupPlanogramApply.displayGroupInfo'),
      key: 'displayGroupInfo',
      render: (_, record: StoreDisplayGroupPlanogramApply) => (
        <div>
          <div>{record.displayGroupCode}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.displayGroupName}</div>
        </div>
      ),
      width: 180,
    },
    {
      title: t('storeDisplayGroupPlanogramApply.applyStatus'),
      dataIndex: 'applyStatus',
      key: 'applyStatus',
      render: renderApplyStatus,
      // sorter: true,
      // sortOrder: pagination.sortBy === 'applyStatus' ? (pagination.sortOrder === 'asc' ? 'ascend' : 'descend') : null,
      width: 150,
    },
    {
      title: t('storeDisplayGroupPlanogramApply.planogramImageUrl'),
      dataIndex: 'planogramImageUrl',
      key: 'planogramImageUrl',
      render: (planogramImageUrl: string) => (
        planogramImageUrl ? (
          <Image
            src={planogramImageUrl + "?x-oss-process=image/resize,w_200,h_150"}
            width={60}
            height={40}
            style={{ objectFit: 'cover', borderRadius: '4px' }}
            fallback={planogramImageUrl}
            preview={
              planogramImageUrl && {
                src: planogramImageUrl,
              }
            }
          />
        ) : (
          <span style={{ color: '#999' }}>-</span>
        )
      ),
      width: 120,
      align: 'center',
    },
    // {
    //   title: t('storeDisplayGroupPlanogramApply.applyPerson'),
    //   key: 'applyPerson',
    //   render: (_, record: StoreDisplayGroupPlanogramApply) => (
    //     <Tooltip title={record.applyPerson}>
    //       {record.applyPersonName}
    //     </Tooltip>
    //   ),
    //   width: 120,
    // },
    {
      title: t('storeDisplayGroupPlanogramApply.applyTime'),
      dataIndex: 'applyTime',
      key: 'applyTime',
      render: formatDateTime,
      // sorter: true,
      // sortOrder: pagination.sortBy === 'applyTime' ? (pagination.sortOrder === 'asc' ? 'ascend' : 'descend') : null,
      width: 180,
    },
    {
      title: t('storeDisplayGroupPlanogramApply.auditInfo'),
      key: 'auditInfo',
      render: (_, record: StoreDisplayGroupPlanogramApply) => (
        record.auditPersonName ? (
          <div>
            <div>{record.auditPersonName}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {formatDateTime(record.auditTime!)}
            </div>
          </div>
        ) : '-'
      ),
      width: 180,
    },
    // {
    //   title: t('storeDisplayGroupPlanogramApply.expectedEndDate'),
    //   dataIndex: 'expectedEndDate',
    //   key: 'expectedEndDate',
    //   render: formatDate,
    //   // sorter: true,
    //   // sortOrder: pagination.sortBy === 'expectedEndDate' ? (pagination.sortOrder === 'asc' ? 'ascend' : 'descend') : null,
    //   width: 180,
    // },
    {
      title: t('storeDisplayGroupPlanogramApply.actualEndDate'),
      dataIndex: 'actualEndDate',
      key: 'actualEndDate',
      render: formatDate,
      // sorter: true,
      // sortOrder: pagination.sortBy === 'actualEndDate' ? (pagination.sortOrder === 'asc' ? 'ascend' : 'descend') : null,
      width: 180,
    },
    {
      title: t('common.actions'),
      key: 'actions',
      fixed: 'right',
      width: 180,
      render: (_, record: StoreDisplayGroupPlanogramApply) => (
        <Space size="small">
          <Tooltip title={t('common.view')}>
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record.id)}
            />
          </Tooltip>
          
          {record.applyStatus === ApplyStatus.PENDING && (
            <>
              <Tooltip title={t('storeDisplayGroupPlanogramApply.audit')}>
                <Button
                  type="link"
                  size="small"
                  icon={<CheckOutlined />}
                  onClick={() => handleAuditApply(record)}
                  loading={auditLoading === record.id}
                />
              </Tooltip>
              
            </>
          )}
        </Space>
      ),
    },
  ];

  // Custom filter panel component
  const renderFilterPanel = () => (
    <FilterPanel
      form={form}
      onFilter={(values) => {
        handleFiltersChange(values);
      }}
      onReset={() => {
        handleReset();
      }}
      loading={loading}
      title={t('menu.sgApplyPlanogram')}
    >
      
      <Col span={6}>
        <Form.Item name="storeGroupCode" label={t('storeDisplayGroupPlanogramApply.storeGroupCode')}>
          <Select
            placeholder={t('storeDisplayGroupPlanogramApply.storeGroupCodePlaceholder')}
            showSearch
            allowClear
            loading={storeGroupOptionsLoading}
            filterOption={(input, option) =>
              option?.children?.toString().toLowerCase().includes(input.toLowerCase()) ?? false
            }
          >
            {storeGroupOptions.map(storeGroup => (
              <Select.Option key={storeGroup.code} value={storeGroup.code}>
                {storeGroup.code} - {storeGroup.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      
      <Col span={6}>
        <Form.Item name="displayGroupCode" label={t('storeDisplayGroupPlanogramApply.displayGroupCode')}>
            <Input placeholder={t('storeDisplayGroupPlanogramApply.displayGroupCodePlaceholder')} />
        </Form.Item>
      </Col>
      
      <Col span={6}>
        <Form.Item name="mainDivisionCode" label={t('common.division')}>
          <Select
            placeholder={t('common.division')}
            showSearch
            allowClear
            loading={divisionOptionsLoading}
            filterOption={(input, option) =>
              option?.children?.toString().toLowerCase().includes(input.toLowerCase()) ?? false
            }
          >
            {divisionOptions.map(division => (
              <Select.Option key={division.value} value={division.value}>
                {division.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      
      <Col span={6}>
        <Form.Item name="applyStatus" label={t('storeDisplayGroupPlanogramApply.applyStatus')}>
          <Select placeholder={t('storeDisplayGroupPlanogramApply.applyStatusPlaceholder')} allowClear>
            <Select.Option value={ApplyStatus.PENDING}>
              {t('storeDisplayGroupPlanogramApply.status.pending')}
            </Select.Option>
            <Select.Option value={ApplyStatus.APPROVED}>
              {t('storeDisplayGroupPlanogramApply.status.approved')}
            </Select.Option>
            <Select.Option value={ApplyStatus.REJECTED}>
              {t('storeDisplayGroupPlanogramApply.status.rejected')}
            </Select.Option>
          </Select>
        </Form.Item>
      </Col>
      
      <Col span={6}>
        <Form.Item name="dateRange" label={t('storeDisplayGroupPlanogramApply.applyTimeRange')}>
          <RangePicker showTime format="YYYY-MM-DD HH:mm:ss" />
        </Form.Item>
      </Col>
    </FilterPanel>
  );

  if (error && isRetryableError(error)) {
    return (
      <NetworkError
        message={error}
        onRetry={handleRetry}
      />
    );
  }

  return (
    <div>

      {renderFilterPanel()}

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => 
            t('common.paginationTotal', { 
              start: range[0], 
              end: range[1], 
              total: total 
            }),
          pageSizeOptions: PAGE_SIZES.map(size => size.toString()),
        }}
        scroll={{ x: 1500, y: 500 }}
        onChange={handleTableChange}
        locale={{
          emptyText: data.length === 0 && !loading ? (
            <EmptyState
              title={t('storeDisplayGroupPlanogramApply.noData')}
              description={error ? t('common.error') : t('storeDisplayGroupPlanogramApply.noData')}
            />
          ) : undefined
        }}
      />

      {/* Audit Modal */}
      <Modal
        title={t('storeDisplayGroupPlanogramApply.auditTitle')}
        open={auditModalVisible}
        onCancel={() => {
          setAuditModalVisible(false);
          setAuditingApply(null);
        }}
        footer={null}
        width={600}
      >
        {auditingApply && (
          <div>
            <div style={{ marginBottom: 16, padding: 16, background: '#fafafa', borderRadius: 4 }}>
              <div><strong>{t('storeDisplayGroupPlanogramApply.applyNo')}:</strong> {auditingApply.applyNo}</div>
              <div><strong>{t('storeDisplayGroupPlanogramApply.storeGroupInfo')}:</strong> {auditingApply.storeGroupCode} - {auditingApply.storeGroupName}</div>
              <div><strong>{t('storeDisplayGroupPlanogramApply.displayGroupInfo')}:</strong> {auditingApply.displayGroupCode} - {auditingApply.displayGroupName}</div>
              <div><strong>{t('storeDisplayGroupPlanogramApply.applyReason')}:</strong> {auditingApply.applyReason}</div>
            </div>
            
            <Form
              form={auditForm}
              layout="vertical"
              onFinish={handleAuditSubmit}
            >
              <Form.Item
                name="applyStatus"
                label={t('storeDisplayGroupPlanogramApply.auditResult')}
                rules={[{ required: true, message: t('storeDisplayGroupPlanogramApply.auditResultRequired') }]}
              >
                <Select placeholder={t('storeDisplayGroupPlanogramApply.auditResultPlaceholder')}>
                  <Select.Option value={ApplyStatus.APPROVED}>
                    {t('storeDisplayGroupPlanogramApply.status.approved')}
                  </Select.Option>
                  <Select.Option value={ApplyStatus.REJECTED}>
                    {t('storeDisplayGroupPlanogramApply.status.rejected')}
                  </Select.Option>
                </Select>
              </Form.Item>
              
              <Form.Item
                name="auditRemark"
                label={t('storeDisplayGroupPlanogramApply.auditRemark')}
                rules={[
                  { max: 500, message: t('storeDisplayGroupPlanogramApply.auditRemarkMaxLength') }
                ]}
              >
                <Input.TextArea
                  rows={4}
                  placeholder={t('storeDisplayGroupPlanogramApply.auditRemarkPlaceholder')}
                />
              </Form.Item>
              
              <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
                <Space>
                  <Button 
                    onClick={() => {
                      setAuditModalVisible(false);
                      setAuditingApply(null);
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button 
                    type="primary" 
                    htmlType="submit"
                    loading={auditLoading === auditingApply.id}
                  >
                    {t('common.submit')}
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default StoreDisplayGroupPlanogramApplyList;